// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Clickable, Hidden, Icon, LoadingSpinner } from "@insite/mobius/index";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import loadSystemList from "@tps/App/Handlers/SystemList/LoadSystemList";
import setCloseAutoComplete from "@tps/App/Handlers/XNG/searchAutoComplete";
import setShowDropShadow from "@tps/App/Handlers/XNG/showDropShadow";
import { CustomAppState, xCarbonStainlessFilterActions, xHeaderActions } from "@tps/App/Store/Reducers";
import { getSystemListSelector } from "@tps/App/Store/Selectors/SystemListSelectors";
import { HeaderSize } from "@tps/Overrides/Pages/Header";
import Search from "@tps/XDL/Icons/Search";
import { color, mergeWithCss, styledScrollbar, xScreen } from "@tps/XDL/Utilities";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        searchInputFocus: state.xHeader.searchInputFocus,
        // Xng-Changes
        closeAutoComplete: state.xHeader.closeAutoComplete,
        isMobileScreen: state.xCommon.isMobileScreen,
        searchAttributesSystemList: getSystemListSelector(state, "HomePageSearchAttribute"),
        filterQuery: state.pages.productList.filterQuery,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    setSearchInputStatus: (focus: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/searchInputFocus", focus });
    },
    setNavDrawerIsOpen,
    setShowDropShadow,
    // Xng-Changes
    setCloseAutoComplete,
    loadSystemList,
    // XNG-Changes
    setCarbonStainlessValue: (selectedValue?: string) => (dispatch: Dispatch<xCarbonStainlessFilterActions>) => {
        dispatch({
            type: "xCarbonStainlessFilter/setSelectedValue",
            selectedValue,
        });
    },
    addProductFilters,
    removeProductFilters,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    // XNG-Changes
    checkboxOption: string;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    searchInputWrapper?: InjectableCss;
    searchInputContainer?: InjectableCss;
    mobileSearchIconWrapper?: InjectableCss;
    autocompleteLeftColumnWrapper?: InjectableCss;
    autocompleteRightColumnWrapper?: InjectableCss;
    iconStyle?: IconPresentationProps;
    checkBoxWrapper?: InjectableCss;
    checkboxGroup?: CheckboxGroupProps;
    checkbox?: CheckboxProps;
}

const sharedSearchInputStyle: { autocompleteCategoriesStyles?: AutocompleteCategoriesStyles } = {
    autocompleteCategoriesStyles: {
        headerText: {
            size: 14,
            weight: "bold",
            color: "text.main",
            ellipsis: true,
            css: css`
                display: flex;
                justify-content: space-between;
                letter-spacing: 0;
                line-height: 19px;
                margin: 20px 0 3px;
                &:first-child {
                    margin-top: 0;
                }
            `,
        },
        link: {
            color: "text.main",
            typographyProps: {
                size: 12,
                weight: "normal",
                ellipsis: true,
                css: css`
                    letter-spacing: 0;
                    line-height: 17px;
                    margin: 0;
                `,
            },
            css: css`
                max-width: 100%;
            `,
        },
        focusedCategoryItemText: {
            size: 12,
            weight: "bold",
            color: "primary.main",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                display: block;
                margin: 5px 0;
                max-width: 100%;
            `,
        },
        focusedLink: {
            color: "text.link",
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 24px;
                    max-width: 100%;
                `,
            },
            css: css`
                max-width: 100%;
            `,
        },
        categoryItemText: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 13px;
                display: block;
                margin: 5px 0;
                max-width: 100%;
            `,
        },
    },
};

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: Search,
            color: "primary",
            size: 15,
        },
        border: "rectangle",
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            formInputWrapper: css`
                input {
                    padding-right: 35px !important;
                    &.remove-bottom-border {
                        border-bottom: 1px solid ${color("common.ui5")} !important;
                    }
                }
                .clickable-icon > span {
                    right: 2px;

                    svg {
                        width: 15px;
                        height: 15px;
                    }
                }
            `,
        },
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
        css: css`
            background-color: ${color("common.white")};
            box-shadow: none;
        `,
    } as any,
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            overflow-x: hidden;
            max-height: 580px;
            border: 1px solid ${color("common.ui5")};
            border-top: 0;

            ${xScreen("lte", "md")} {
                border-top: 1px solid ${color("common.border")};
                border-inline: 0;
                padding-left: 17px;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column;
                max-height: 100vh;
                height: auto;
                padding: 0 3px;
            }

            ${xScreen("lte", "md")} {
                max-height: calc(100vh - ${HeaderSize.mobile}px);
                height: auto;
                ${styledScrollbar(4)}
            }

            &:empty {
                display: none;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    searchInputContainer: {
        css: css`
            width: 100%;
        `,
    },
    searchInputWrapper: {
        css: css`
            width: 100%;
            ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                `
                &&& {
                    .close-search-btn, .loader {
                        position: absolute;
                        top: 10px;
                        right: -36px;
                    }
                }
            `}

            ${({ searchInputFocus }: any) => !searchInputFocus && `display: none;`}
            ${xScreen("gte", "md")} {
                display: block;
            }
        `,
    },
    mobileSearchIconWrapper: {
        css: css`
            margin-left: 19px;
            margin-right: 8px;
            ${xScreen("gte", "md")} {
                display: none;
            }
            ${({ searchInputFocus }: any) => searchInputFocus && `display: none;`}
        `,
    },
    autocompleteLeftColumnWrapper: {
        css: css`
            width: 45%;
            padding: 13px 13px 7px;
            text-align: left;
            position: relative;
            &:after {
                content: " ";
                position: absolute;
                height: 100%;
                width: 1px;
                background-color: ${color("common.ui5")};
                right: 0;
                top: 0;
            }
            &:not(.split-two-views) {
                width: 100%;
                &:after {
                    background: transparent;
                }
            }
            ${xScreen("lt", "sm")} {
                width: 100%;
                &:after {
                    background: transparent;
                }
            }
        `,
    },
    autocompleteRightColumnWrapper: {
        css: css`
            width: 55%;
            padding-left: 13px;
            text-align: left;
            &:not(.split-two-views) {
                width: 100%;
            }
            ${xScreen("lt", "sm")} {
                width: 100%;
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...mergeWithCss(sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText, {
                css: css`
                    margin-bottom: 10px;
                `,
            }),
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedCategoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        categoryItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedBrandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedCategoryItemText,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
        brandItemText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.categoryItemText,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.headerText,
        },
        link: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.link,
        },
        focusedLink: {
            ...sharedSearchInputStyle.autocompleteCategoriesStyles?.focusedLink,
        },
    },
    iconStyle: {
        size: 18,
        src: "Search",
        color: "primary",
        css: css`
            margin-top: 4px;
        `,
    },
    checkBoxWrapper: {
        css: css`
            margin-top: 6px;
            ${xScreen("lte", "md")} {
                margin-top: 3px;
            }
        `,
    },
    checkboxGroup: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 12px;
        `,
    },
    checkbox: {
        css: css`
            &&&& {
                margin: 0;
            }
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeWithCss(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            checkboxOption: "",
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;
    searchHistory = getSearchHistory();

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            // XNG-Changes
            // this.updateFocusableItems();
        }
        if (this.props.closeAutoComplete) {
            this.closeAutoCompleteAndPopover();
            this.props.setCloseAutoComplete({ closeAutoComplete: false });
        }
    }

    // componentDidMount() {
    //     window.addEventListener("resize", this.handleResize, true);
    // }

    // componentWillUnmount() {
    //     window.removeEventListener("resize", this.handleResize);
    // }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        // XNG-Changes
        const matchedItem = this.props.searchAttributesSystemList?.values?.find(
            item => item?.description?.toLowerCase() === this.state.checkboxOption?.toLowerCase(),
        );

        const attributeValueIds = matchedItem ? matchedItem.description : undefined;

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(
                `/${this.props.searchPath}?query=${encodedQuery}${
                    attributeValueIds ? `&attributeValueIds=${attributeValueIds}` : ""
                }`,
                false,
            );

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
        this.inputFocusStoreReset();
    };

    loadAutocomplete = debounce((query: string) => {
        // XNG-changes: Don't load results if autocomplete is not shown
        if (!this.showAutocomplete) {
            return;
        }
        // XNG-changes: Prepend checkbox option if present
        if (query.length > 0) {
            this.setState({ isLoading: true });
        }

        // XNG-Changes
        const matchedItem = this.props.searchAttributesSystemList?.values?.find(
            item => item?.description?.toLowerCase() === this.state.checkboxOption?.toLowerCase(),
        );

        this.props.getAutocompleteModel({
            query,
            searchWithin: matchedItem?.additionalInfo || matchedItem?.name || undefined,
            onSuccess: (result: AutocompleteModel) => {
                this.setState({ isLoading: false });
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                    });
                    this.openAutocomplete();
                }
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        } as any);
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    openAutocomplete = () => {
        document.body.classList.add("lock");
        this.popover.current && (this.popover.current! as any).openPopover();

        const inputElement = document.getElementsByClassName("header-search-input")?.[1];
        if (inputElement) {
            inputElement.classList.add("remove-bottom-border");
        }
    };

    closeAutocomplete = () => {
        this.setState({ autoCompleteModel: undefined });
        this.popover.current && (this.popover.current! as any).closePopover();
        this.inputFocusStoreReset();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        const headerElement = document.getElementsByTagName("header")[0].getBoundingClientRect();
        const inputElement = document.getElementsByClassName("header-search-input")[1].getBoundingClientRect();

        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        const windowSize = window.innerWidth;
        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.left = `${inputElement.x}px`;
            positionStyle.width = `${inputElement.width}px`;
            // XNG-Changes             
            positionStyle.top = `${headerElement.height - 24}px`;
            if (windowSize <= 1024) {
                positionStyle.top = headerElement.height;
                positionStyle.left = "0";
                positionStyle.width = "100%";
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                // eslint-disable-next-line no-case-declarations
                const searchId = `headerSearch-${this.props.id}-${uniqueId()}`;
                document?.getElementById(searchId)?.blur();
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = (event?: Event) => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });

        if (event) {
            const inputElement = document.getElementsByClassName("header-search-input")?.[1];
            const searchWrapper = document.getElementById("search-input");

            // XNG-Changes: Prevent close search input popover when press checkboxes on mobile
            if (
                inputElement !== event.target &&
                event.type !== "keydown" &&
                (!this.props.isMobileScreen || !searchWrapper?.contains(event.target as Node))
            ) {
                this.inputFocusStoreReset();
            }
        }

        const inputElement = document.getElementsByClassName("header-search-input")?.[1];
        if (inputElement) {
            inputElement.classList?.remove("remove-bottom-border");
        }
    };

    handleClickOutside = (event: MouseEvent) => {
        const windowSize = window.innerWidth;
        const inputElement = document.getElementsByClassName("header-search-input")?.[1];
        const searchWrapperElement = document.getElementsByClassName("header-search-input-wrapper")?.[1];
        if (!searchWrapperElement?.contains(event.target as Node) && inputElement !== event.target) {
            if (windowSize >= 768) {
                this.showAutocomplete = false;
                this.setState({ autoCompleteModel: undefined });
            } else {
                const searchIconElement = document.getElementsByClassName("header-search-input-wrapper")?.[0];
                const searchWrapper = document.getElementById("search-input");

                // XNG-Changes: Prevent close search input popover when press checkboxes on mobile
                if (
                    !searchIconElement?.contains(event.target as Node) &&
                    (!this.props.isMobileScreen || !searchWrapper?.contains(event.target as Node))
                ) {
                    this.executeCancelSearch(event);
                } else {
                    this.showAutocomplete = true;
                }
            }
        }
    };

    trackAutocompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutocompleteSearchResults();
        }
        this.closeAutocomplete();
        this.showAutocomplete = false;
        this.setState({
            query: "",
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    closeNavDrawer = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };

    inputFocusSet = (event?: any) => {
        document.body.classList.add("lock");
        this.showAutocomplete = true;
        this.props.setShowDropShadow({ showDropShadow: true });
        this.closeNavDrawer();
        this.props.setSearchInputStatus(true);
        // Refresh autocomplete results when input is focused with existing query
        if (this.state.query) {
            this.loadAutocomplete(this.state.query);
        }
    };

    inputFocusStoreReset = () => {
        document.body.classList.remove("lock");
        this.props.setSearchInputStatus(false);
        this.props.setShowDropShadow({ showDropShadow: false });
    };

    closeAutoCompleteAndPopover = () => {
        this.handlePopoverClose();
        this.closeAutocomplete();
        this.showAutocomplete = false;
    };

    executeCancelSearch = (event: any) => {
        this.inputFocusStoreReset();
        this.handlePopoverClose();
        this.closeAutocomplete();
        this.showAutocomplete = false;
        this.setState({ autoCompleteModel: undefined });
    };

    closeIconClick = (event: any) => {
        this.executeCancelSearch(event);
        this.setState({ query: "", autoCompleteModel: undefined });
    };

    // XNG-Changes
    handleCheckboxChange = (selectedValue: string, isSelected: boolean) => {
        this.setState(prev => {
            const isSameOption = prev.checkboxOption === selectedValue;
            const newCheckboxOption = isSameOption ? "" : selectedValue;

            // If we have a query, reload autocomplete with the new checkbox selection
            if (prev.query) {
                this.showAutocomplete = true;
                this.loadAutocomplete(prev.query);
            }

            // XNG-Changes: Sync Carbon/Stainless checkbox with product attribute filters
            this.props.setCarbonStainlessValue(newCheckboxOption || undefined);

            if (prev.checkboxOption) {
                this.props.removeProductFilters({ attributeValueIds: [prev.checkboxOption] });
            }
            if (!isSameOption && !this.props.filterQuery?.includes(selectedValue)) {
                this.props.addProductFilters({ attributeValueIds: [selectedValue] });
            }
            return { checkboxOption: newCheckboxOption };
        });
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel || this.state.isLoading) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;

        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);

        if (!resultCount) {
            return null;
        }
        return (
            <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper
                        {...this.styles.autocompleteLeftColumnWrapper}
                        className={`autocomplete-column-wrapper ${
                            products && products.length > 0 ? "split-two-views" : ""
                        }`}
                    >
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                        />
                        <AutocompleteBrands
                            brands={brands}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteBrandsStyles}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {products && products.length > 0 && (
                    <StyledWrapper
                        {...this.styles.autocompleteRightColumnWrapper}
                        className={`autocomplete-column-wrapper ${
                            (categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)
                                ? "split-two-views"
                                : ""
                        }`}
                    >
                        <AutocompleteProducts
                            products={products}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteProductsStyles}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}-${uniqueId()}`;
        const searchInputFocus = this.props.searchInputFocus;
        const searchInput = (
            <StyledWrapper {...styles.searchInputContainer}>
                <StyledWrapper {...({ searchInputFocus } as any)} {...styles.searchInputWrapper}>
                    <TextField
                        {...styles.input}
                        ref={this.props.inputRef}
                        iconClickableProps={{
                            onClick: () => {
                                this.inputFocusSet();
                                this.doSearch();
                            },
                            className: "clickable-icon",
                            "aria-roledescription": `Submit Search`,
                            id: `headerSearchButton_${this.props.id}-${uniqueId()}`,
                            color: "primary",
                        }}
                        value={this.state.query}
                        onChange={this.handleInputChange}
                        onFocus={this.inputFocusSet}
                        // XNG-Change: change placeholder text
                        placeholder={translate("Search by Keyword")}
                        autoComplete="off"
                        clickableText={translate("submit search")}
                        id={searchId}
                        data-test-selector="headerSearchInputTextField"
                        className="header-search-input"
                        aria-multiline="false"
                        aria-required="false"
                        {...({ searchInputFocus } as any)}
                        maxLength={100}
                    />
                    {searchInputFocus && (
                        <Hidden above="md">
                            {this.state.isLoading ? (
                                <StyledWrapper
                                    css={css`
                                        height: 17px;
                                        display: flex;
                                        align-items: center;
                                        svg {
                                            width: 22px;
                                            height: 22px;
                                        }
                                    `}
                                    className="loader"
                                >
                                    <LoadingSpinner />
                                </StyledWrapper>
                            ) : (
                                <Clickable
                                    aria-label="Close"
                                    className="close-search-btn"
                                    onClick={this.closeIconClick}
                                >
                                    <Icon size={15} src="Close" color="common.ui5" key="close-search" />
                                    <VisuallyHidden>{translate("close-search")}</VisuallyHidden>
                                </Clickable>
                            )}
                        </Hidden>
                    )}
                </StyledWrapper>
                {!searchInputFocus && (
                    <StyledWrapper {...({ searchInputFocus } as any)} {...styles.mobileSearchIconWrapper}>
                        <Clickable
                            onClick={() => {
                                this.inputFocusSet();
                                this.doSearch();
                            }}
                        >
                            <Icon {...styles.iconStyle} key="search" />
                            <VisuallyHidden>{translate("search")}</VisuallyHidden>
                        </Clickable>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );

        return (
            <StyledWrapper className="header-search-input-wrapper">
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    handleClickOutside={this.handleClickOutside}
                    className="popover"
                >
                    {!this.state.query && this.showAutocomplete && this.searchHistory?.length > 0 && (
                        <StyledWrapper {...this.styles.autocompleteWrapper}>
                            <SearchHistory
                                focusedQuery={this.state.focusedSearchHistoryQuery}
                                goToUrl={this.goToUrl}
                                extendedStyles={styles.searchHistoryStyles}
                                {...({ closeAutoCompleteAndPopover: this.closeAutoCompleteAndPopover } as any)}
                            />
                        </StyledWrapper>
                    )}
                    {this.state.query && this.showAutocomplete && this.renderAutocomplete()}
                </Popover>
                {this.props.searchAttributesSystemList?.values &&
                    this.props.searchAttributesSystemList.values.length > 0 &&
                    (!this.props.isMobileScreen || searchInputFocus) && (
                        <StyledWrapper {...styles.checkBoxWrapper}>
                            <CheckboxGroup {...styles.checkboxGroup}>
                                {this.props.searchAttributesSystemList.values?.map(item => {
                                    if (!item) {
                                        return;
                                    }
                                    const displayValue = item.additionalInfo || item.name || "";
                                    const termValue = item.description || "";

                                    return (
                                        <Checkbox
                                            key={`searchCheckbox-${termValue}`}
                                            {...styles.checkbox}
                                            checked={this.state.checkboxOption === termValue}
                                            // XNG-Changes
                                            onChange={() =>
                                                this.handleCheckboxChange(
                                                    termValue,
                                                    this.state.checkboxOption !== termValue,
                                                )
                                            }
                                        >
                                            {translate(displayValue)}
                                        </Checkbox>
                                    );
                                })}
                            </CheckboxGroup>
                        </StyledWrapper>
                    )}
            </StyledWrapper>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
